import { Injectable, Signal, WritableSignal, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { PageKey } from './models/page.key';
import { buildDocumentTitle } from './page-title.util';

@Injectable({
  providedIn: 'root'
})
export class PageService {
  constructor(
    private readonly browserTitle: Title,
    router: Router,
    route: ActivatedRoute
  ) {
    this.applyRouteContext(route);

    router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => this.applyRouteContext(route));
  }

  private readonly _page: WritableSignal<PageKey | null> = signal(null);

  public get page(): Signal<PageKey | null> {
    return this._page;
  }

  public set page(page: PageKey) {
    this._page.set(page);
  }

  private applyRouteContext(route: ActivatedRoute): void {
    const activeRoute = this.getActiveRoute(route);
    const pageKey = this.resolvePageKey(activeRoute);
    this._page.set(pageKey);
    this.browserTitle.setTitle(buildDocumentTitle(this.resolvePageTitle(activeRoute)));
  }

  private resolvePageTitle(route: ActivatedRoute): string | undefined {
    let current: ActivatedRoute | null = route;
    while (current) {
      const title = current.snapshot.data['title'];
      if (typeof title === 'string' && title.trim()) return title.trim();
      current = current.parent;
    }
    return undefined;
  }

  private resolvePageKey(route: ActivatedRoute): PageKey | null {
    let current: ActivatedRoute | null = route;
    while (current) {
      const key = current.snapshot.data['key'];
      if (key) return key as PageKey;
      current = current.parent;
    }
    return null;
  }

  private getActiveRoute(route: ActivatedRoute): ActivatedRoute {
    while (route.firstChild) route = route.firstChild;
    return route;
  }
}
